import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import { UMPaper, UMWrapper } from "./Atom"
import Layout from "../../components/Layout"

import { UserManagementHeader } from "../../components/Headers"
import { UserManagementTabs } from "../../components/Tabs"

import { setLoading, setOpenSidebar } from "../../redux/slices/appSlice"
import { getTokenLocal } from "../../../helper/utils"
import { setUsersData } from "../../redux/slices/usersSlice"
import UsersAPI from "../../../api/users"
import CreateNewUserModal from "../../components/Modal/CreateNewUserModal"
import UserAPI from "../../../api/user"
import { setPerms, setRoleCategories, setRoles } from "../../redux/slices/rolesAndPermsSlice"
import { useResponseHandle } from "../../hooks"
import { useNavigate } from "react-router"
import { useSelector } from "react-redux"
import AuthAPI from "../../../api/auth"

const usersAPI = new UsersAPI()
const rolesAndPermsAPI = new UserAPI()
const authAPI = new AuthAPI()

const UserManagement = () => {
  const usersData = useSelector((state: any) => state.users?.usersData)

  const dispatch = useDispatch()
  dispatch(setOpenSidebar(false))

  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const token: any = getTokenLocal()

  const fetchUsersData = async () => {
    dispatch(setLoading(true))

    const response = await usersAPI.getAllUsers(token)

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      dispatch(setUsersData(result?.data))
    } else {
      navigate("/auth")
    }

    dispatch(setLoading(false))

    return result
  }

  const fetchAllRolesAndPerms = async () => {
    if (token) {
      const response = await rolesAndPermsAPI.getRolesAndPermissionsInfo(token)
      const result = useResponseHandle({
        apiResponse: response,
        successMessage: false,
      })

      if (result?.isAuthorized) {
        dispatch(setRoles(result?.data))
        const currentModules: any = []

        result?.data?.[0]?.modules?.forEach((module: any) => {
          currentModules.push({
            ...module,
            role: response?.data?.data?.[0]?.role_id,
            open: false,
          })
        })

        dispatch(setPerms(currentModules))
      } else {
        navigate("/auth")
      }
    }
  }

  const fetchAllRoleCategories = async () => {
    if (token) {
      const response = await usersAPI.getAllRoleCategories(token)
      const result = useResponseHandle({
        apiResponse: response,
        successMessage: false,
      })

      if (result?.isAuthorized) {
        if (result?.statusCode == 200) {
          dispatch(setRoleCategories(result?.data))
        }
      } else {
        navigate("/auth")
      }
    }
  }

  const sendInvitation = async (data: any) => {
    if (token) {
      const response = await authAPI.userCreate(token, data)
      const result = useResponseHandle({
        apiResponse: response,
        successMessage: false,
      })

      if (result?.isAuthorized) {
        await fetchUsersData()
        return response
      } else {
        navigate("/auth")
      }
    }
  }

  const deactivateUser = async (uid: any) => {
    const response = await usersAPI.updateUserStatus(token, uid, "inactive")
    const result = useResponseHandle({
      apiResponse: response,
      successMessage: true,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const newUserList: any = []

        usersData?.forEach((user: any) => {
          if (user?.uid == uid) {
            newUserList.push({ ...user, status: "inactive" })
          } else {
            newUserList.push({ ...user })
          }
        })

        dispatch(setUsersData(newUserList))
      }
    } else {
      navigate("/auth")
    }

    return result
  }

  const reactivateUser = async (uid: any) => {
    const response = await usersAPI.updateUserStatus(token, uid, "active")
    const result = useResponseHandle({
      apiResponse: response,
      successMessage: true,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const newUserList: any = []

        usersData?.forEach((user: any) => {
          if (user?.uid == uid) {
            newUserList.push({ ...user, status: "active" })
          } else {
            newUserList.push({ ...user })
          }
        })

        dispatch(setUsersData(newUserList))
      }
    } else {
      navigate("/auth")
    }
    return result
  }

  useEffect(() => {
    fetchUsersData()
    fetchAllRolesAndPerms()
    fetchAllRoleCategories()
  }, [])

  return (
    <UMWrapper>
      <CreateNewUserModal
        fetchUsersData={fetchUsersData}
        open={open}
        setOpen={setOpen}
        sendInvitation={sendInvitation}
      />
      <UserManagementHeader setOpen={setOpen} name="User Management" />
      <UMPaper>
        <UserManagementTabs
          fetchAllRolesAndPerms={fetchAllRolesAndPerms}
          deactivateUser={deactivateUser}
          reactivateUser={reactivateUser}
        />
      </UMPaper>
    </UMWrapper>
  )
}

export default UserManagement
